import { useMutation } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useUpdateConversationParticipant from "common/datahooks/useUpdateConversationParticipant";
import useUpdateDetailedLead from "common/datahooks/useUpdateDetailedLead";
import useUpdateLead from "common/datahooks/useUpdateLead";
import { del, post } from "common/helpers/HTTP";
import type { Lead } from "common/types";

type UpdateCacheOn = "mutate" | "success";

interface FavoriteMutationVariables {
  leadId: string;
  abortSignal?: AbortSignal;
  updateCacheOn?: UpdateCacheOn;
}

async function addFavoriteRequest(
  workspaceId: string,
  leadId: string,
  abortSignal?: AbortSignal,
) {
  await post(
    `workspaces/${workspaceId}/leads/${leadId}/favorite`,
    undefined,
    abortSignal,
  );
}

async function removeFavoriteRequest(
  workspaceId: string,
  leadId: string,
  abortSignal?: AbortSignal,
) {
  await del(`workspaces/${workspaceId}/leads/${leadId}/favorite`, abortSignal);
}

export default function useFavorite() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { updateLead } = useUpdateLead();
  const { updateDetailedLead } = useUpdateDetailedLead();
  const { updateConversationParticipant } = useUpdateConversationParticipant();

  async function updateCachedLead(leadId: string, isFavorite: boolean) {
    function updateFunction(lead: Pick<Lead, "favorite">) {
      lead.favorite = isFavorite;
    }
    await updateLead(leadId, updateFunction);
    await updateDetailedLead(leadId, updateFunction);
    await updateConversationParticipant(leadId, updateFunction);
  }

  const { mutateAsync: addFavorite } = useMutation({
    mutationFn: ({ leadId, abortSignal }: FavoriteMutationVariables) =>
      addFavoriteRequest(workspaceId, leadId, abortSignal),
    onMutate: async ({ leadId, updateCacheOn = "mutate" }) => {
      if (updateCacheOn === "mutate") {
        await updateCachedLead(leadId, true);
      }
    },
    onSuccess: async (_, { leadId, updateCacheOn = "mutate" }) => {
      if (updateCacheOn === "success") {
        await updateCachedLead(leadId, true);
      }
    },
    onError: async (_error, { leadId, updateCacheOn = "mutate" }) => {
      if (updateCacheOn === "mutate") {
        await updateCachedLead(leadId, false);
      }
    },
  });

  const { mutateAsync: removeFavorite } = useMutation({
    mutationFn: ({ leadId, abortSignal }: FavoriteMutationVariables) =>
      removeFavoriteRequest(workspaceId, leadId, abortSignal),
    onMutate: async ({ leadId, updateCacheOn = "mutate" }) => {
      if (updateCacheOn === "mutate") {
        await updateCachedLead(leadId, false);
      }
    },
    onSuccess: async (_, { leadId, updateCacheOn = "mutate" }) => {
      if (updateCacheOn === "success") {
        await updateCachedLead(leadId, false);
      }
    },
    onError: async (_error, { leadId, updateCacheOn = "mutate" }) => {
      if (updateCacheOn === "mutate") {
        await updateCachedLead(leadId, true);
      }
    },
  });

  return {
    addFavorite,
    removeFavorite,
  };
}
